/**
 * <img> wrapper that times card-image loads and reports them to telemetry.
 *
 * Drop-in replacement for bare `<img src={card.image_url} alt={card.name} ... />`
 * used across CardDetail, CardGrid, CardDetailPanel, ComparisonPanel.
 *
 * The slow-image-load rule in the watchdog depends on this wrapper being used
 * everywhere — a bare <img> will still render fine but will be invisible to
 * the monitoring system.
 */

import { useRef, type ImgHTMLAttributes } from 'react';
import { reportImageError, reportImageLoad } from '../lib/telemetry';

type CardImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
};

export function CardImage(props: CardImageProps) {
  const { onLoad, onError, src, ...rest } = props;
  const startedAt = useRef<number>(performance.now());

  return (
    <img
      {...rest}
      src={src}
      onLoad={(e) => {
        const img = e.currentTarget;
        const duration = performance.now() - startedAt.current;
        reportImageLoad(src, duration, {
          natural_w: img.naturalWidth,
          natural_h: img.naturalHeight,
        });
        onLoad?.(e);
      }}
      onError={(e) => {
        reportImageError(src);
        onError?.(e);
      }}
    />
  );
}
